import React, { useMemo } from 'react';
import { Calendar, Trophy, Star } from 'lucide-react';
import FilterModalWrapper from './FilterModalWrapper';

/**
 * WeekModal - Desktop modal for selecting NFL week
 * Provides week selection for NFL season games
 * Adapted from mobile WeekSheet
 *
 * @param {boolean} isOpen - Whether modal is open
 * @param {Function} onClose - Callback to close modal
 * @param {string} currentWeek - Currently selected week ID
 * @param {Function} onWeekChange - Callback when week is selected
 * @param {number} seasonYear - Current season year
 */
const WeekModal = ({
  isOpen,
  onClose,
  currentWeek,
  onWeekChange,
  seasonYear = 2025
}) => {
  // Generate week options based on season structure
  const weekOptions = useMemo(() => {
    const weeks = [];

    // Preseason weeks (typically 3-4 weeks)
    weeks.push({
      id: 'preseason',
      label: 'All Preseason',
      icon: Calendar,
      type: 'preseason',
      description: 'All preseason games'
    });

    // Regular season weeks (18 weeks)
    for (let i = 1; i <= 18; i++) {
      weeks.push({
        id: `week${i}`,
        label: `Week ${i}`,
        icon: Calendar,
        type: 'regular',
        description: i === 18 ? 'Final regular season week' : `Regular season week ${i}`
      });
    }

    // Playoff weeks
    weeks.push(
      {
        id: 'wildcard',
        label: 'Wild Card',
        icon: Trophy,
        type: 'playoff',
        description: 'Wild Card round'
      },
      {
        id: 'divisional',
        label: 'Divisional',
        icon: Trophy,
        type: 'playoff',
        description: 'Divisional round'
      },
      {
        id: 'conference',
        label: 'Conference',
        icon: Trophy,
        type: 'playoff',
        description: 'Conference championships'
      },
      {
        id: 'superbowl',
        label: 'Super Bowl',
        icon: Star,
        type: 'playoff',
        description: `Super Bowl ${seasonYear === 2024 ? 'LIX' : 'LVIII'}`
      }
    );

    return weeks;
  }, [seasonYear]);

  // Group weeks by type for better organization
  const groupedWeeks = useMemo(() => {
    return {
      current: weekOptions.filter(w => w.id === currentWeek),
      preseason: weekOptions.filter(w => w.type === 'preseason'),
      regular: weekOptions.filter(w => w.type === 'regular'),
      playoff: weekOptions.filter(w => w.type === 'playoff')
    };
  }, [weekOptions, currentWeek]);

  const handleWeekSelect = (weekId) => {
    // Find the selected week option to get its type
    const selectedOption = weekOptions.find(w => w.id === weekId);

    // Pass back structured data
    onWeekChange({
      weekId,
      seasonType: selectedOption?.type === 'preseason' ? 'PRE' :
                  selectedOption?.type === 'regular' ? 'REG' :
                  selectedOption?.type === 'playoff' ? 'POST' : null,
      weekNumber: weekId.startsWith('week') ? parseInt(weekId.replace('week', '')) : null,
      stageName: ['wildcard', 'divisional', 'conference', 'superbowl'].includes(weekId) ? weekId : null
    });
  };

  return (
    <FilterModalWrapper
      isOpen={isOpen}
      onClose={onClose}
      title="Select Week"
      icon={Trophy}
      maxWidth="md"
      maxHeight="70vh"
    >
      <div className="p-4">
        {/* Current Week (if selected) */}
        {groupedWeeks.current.length > 0 && (
          <div className="mb-4">
            <div className="text-xs text-gray-500 font-medium uppercase px-3 py-2">Current</div>
            {groupedWeeks.current.map((week) => {
              const Icon = week.icon;
              return (
                <button
                  key={week.id}
                  onClick={() => handleWeekSelect(week.id)}
                  className="w-full flex items-center gap-3 p-3 rounded-lg bg-emerald-500/20 border border-emerald-500/50"
                >
                  <div className="p-2 rounded-lg bg-emerald-500/20">
                    <Icon className="w-5 h-5 text-emerald-400" />
                  </div>
                  <div className="flex-1 text-left">
                    <div className="font-medium text-sm text-emerald-400">
                      {week.label}
                    </div>
                    <div className="text-xs text-gray-500 mt-0.5">
                      {week.description}
                    </div>
                  </div>
                  <div className="w-2 h-2 rounded-full bg-emerald-400" />
                </button>
              );
            })}
          </div>
        )}

        {/* Preseason */}
        {groupedWeeks.preseason.length > 0 && (
          <div className="mb-4">
            <div className="text-xs text-gray-500 font-medium uppercase px-3 py-2">Preseason</div>
            {groupedWeeks.preseason.map((week) => {
              const Icon = week.icon;
              const isSelected = currentWeek === week.id;

              return (
                <button
                  key={week.id}
                  onClick={() => handleWeekSelect(week.id)}
                  className={`
                    w-full flex items-center gap-3 p-3 rounded-lg transition-all
                    ${isSelected
                      ? 'bg-emerald-500/20 border border-emerald-500/50'
                      : 'hover:bg-gray-800/50'
                    }
                  `}
                >
                  <div className={`p-2 rounded-lg ${isSelected ? 'bg-emerald-500/20' : 'bg-gray-800'}`}>
                    <Icon className={`w-5 h-5 ${isSelected ? 'text-emerald-400' : 'text-gray-400'}`} />
                  </div>
                  <div className="flex-1 text-left">
                    <div className={`font-medium text-sm ${isSelected ? 'text-emerald-400' : 'text-white'}`}>
                      {week.label}
                    </div>
                    <div className="text-xs text-gray-500 mt-0.5">
                      {week.description}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        )}

        {/* Regular Season */}
        <div className="mb-4">
          <div className="text-xs text-gray-500 font-medium uppercase px-3 py-2">Regular Season</div>
          <div className="grid grid-cols-3 gap-2">
            {groupedWeeks.regular.map((week) => {
              const isSelected = currentWeek === week.id;
              const weekNum = parseInt(week.id.replace('week', ''));

              return (
                <button
                  key={week.id}
                  onClick={() => handleWeekSelect(week.id)}
                  className={`
                    p-3 rounded-lg transition-all text-center
                    ${isSelected
                      ? 'bg-emerald-500/20 border border-emerald-500/50'
                      : 'hover:bg-gray-800/50 border border-gray-800'
                    }
                  `}
                >
                  <div className={`font-medium text-sm ${isSelected ? 'text-emerald-400' : 'text-white'}`}>
                    Week {weekNum}
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Playoffs */}
        {groupedWeeks.playoff.length > 0 && (
          <div className="mb-2">
            <div className="text-xs text-gray-500 font-medium uppercase px-3 py-2">Playoffs</div>
            {groupedWeeks.playoff.map((week) => {
              const Icon = week.icon;
              const isSelected = currentWeek === week.id;

              return (
                <button
                  key={week.id}
                  onClick={() => handleWeekSelect(week.id)}
                  className={`
                    w-full flex items-center gap-3 p-3 rounded-lg transition-all mb-2
                    ${isSelected
                      ? 'bg-emerald-500/20 border border-emerald-500/50'
                      : 'hover:bg-gray-800/50'
                    }
                  `}
                >
                  <div className={`p-2 rounded-lg ${isSelected ? 'bg-emerald-500/20' : 'bg-gray-800'}`}>
                    <Icon className={`w-5 h-5 ${isSelected ? 'text-emerald-400' : 'text-amber-400'}`} />
                  </div>
                  <div className="flex-1 text-left">
                    <div className={`font-medium text-sm ${isSelected ? 'text-emerald-400' : 'text-white'}`}>
                      {week.label}
                    </div>
                    <div className="text-xs text-gray-500 mt-0.5">
                      {week.description}
                    </div>
                  </div>
                  {isSelected && (
                    <div className="w-2 h-2 rounded-full bg-emerald-400" />
                  )}
                </button>
              );
            })}
          </div>
        )}
      </div>
    </FilterModalWrapper>
  );
};

export default WeekModal;
